import {
  Button,
  ChevronDownSVG,
  ChevronUpSVG,
  Chip,
  ChipGroup,
  Icon,
} from "@newsela/angelou";

import { withTrackEvent } from "@newsela/snitch";
import { useFilterControls } from "@/search/Langston/useFilterControls";
import { useStandardsSearchContext } from "@/search/Langston/StandardsSearchProvider";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";
import { useConceptTaxonomyProvider } from "@/search/Langston/ConceptTaxonomyProvider";
import { eventProperties } from "./FilterControls.constants";
import {
  clearButtonCss,
  expandCollapseButtonCss,
  expandCollapseContainerCss,
  expandCollapseCss,
  expandCollapseIconCss,
  filterControlsContainerCss,
  headerText,
  headingCss,
  selectedFilterCss,
  selectedFiltersChipGroupCss,
} from "./FilterControls.styles";
import {
  getFilterChipText,
  getMaturityFilterChipValue,
} from "./FilterControls.utils";

const ButtonWithTrackEvent = withTrackEvent(Button);
const minParam = LangstonParameterSchema.minimum_maturity.key;
const maxParam = LangstonParameterSchema.maximum_maturity.key;

export const FilterControls = () => {
  const {
    allowCollapseFilters,
    filteredChipsToDisplay,
    showAllSelectedFilters,
    handleClearAllFilters,
    handleRemoveFilter,
    handleToggleShowFilters,
    MATURITY_FILTER_VALUE,
  } = useFilterControls();

  const {
    getters: { readingSkills = [], searchParams },
    handlers: { getSingleSearchParamValue },
  } = useSearchPageContext();
  const { selectedStandards } = useStandardsSearchContext();
  const { allTagsRawData } = useConceptTaxonomyProvider();

  if (filteredChipsToDisplay.length === 0) return null;

  const hasMaturityFilter =
    !!getSingleSearchParamValue(minParam) &&
    !!getSingleSearchParamValue(maxParam);

  return (
    <div css={filterControlsContainerCss}>
      <div css={headingCss}>
        <h2 css={headerText}>Selected filters</h2>
        <>
          <ButtonWithTrackEvent
            __cssFor={clearButtonCss}
            flavor={Button.flavors.tertiary}
            onClick={handleClearAllFilters}
            size={Button.sizes.sm}
            {...eventProperties("clear")}
            dataProps={{ "data-qa-selector": "search-clear-all-filters" }}
          >
            Clear filters
          </ButtonWithTrackEvent>
        </>
      </div>

      <ChipGroup
        size="sm"
        groupName="selectedFilters"
        onChipEvent={handleRemoveFilter}
        __cssFor={selectedFiltersChipGroupCss}
        chipType={ChipGroup.chipTypes.input}
      >
        {filteredChipsToDisplay.map(
          ([paramName, paramValue]: [string, string], index: number) => {
            if (paramName === minParam || paramName === maxParam) return "";
            const filterLabelValue = getFilterChipText(
              paramName,
              paramValue,
              readingSkills,
              selectedStandards,
              allTagsRawData,
            );
            return (
              <Chip
                key={index}
                text={filterLabelValue}
                value={`${paramName},${paramValue}`}
                __cssFor={selectedFilterCss(filterLabelValue === "true")}
                dataProps={{
                  "data-qa-selector": `search-${filterLabelValue}-chip`,
                }}
              />
            );
          },
        )}
        {hasMaturityFilter && (
          <Chip
            key={filteredChipsToDisplay.length}
            text={getMaturityFilterChipValue(searchParams)}
            value={MATURITY_FILTER_VALUE}
            __cssFor={selectedFilterCss(false)}
            dataProps={{
              "data-qa-selector": "deselect-search-maturity-range-chip",
            }}
          />
        )}
      </ChipGroup>

      {allowCollapseFilters && (
        <div css={expandCollapseContainerCss}>
          <ButtonWithTrackEvent
            size={Button.sizes.sm}
            __cssFor={expandCollapseButtonCss}
            flavor={Button.flavors.tertiary}
            onClick={handleToggleShowFilters}
            {...eventProperties(showAllSelectedFilters ? "collapse" : "show")}
            dataProps={{
              "data-qa-selector": `search-${showAllSelectedFilters ? "collapse" : "show"}-filter-chips`,
            }}
          >
            <span css={expandCollapseCss}>
              {showAllSelectedFilters ? "Collapse" : "View all"} filters
              <Icon
                __cssFor={{ root: expandCollapseIconCss }}
                title="Expand filters/Collapse filters"
                SvgComponent={
                  showAllSelectedFilters ? ChevronUpSVG : ChevronDownSVG
                }
              />
            </span>
          </ButtonWithTrackEvent>
        </div>
      )}
    </div>
  );
};
